import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/config";
import { Input } from "@/components/ui/input";
import { CheckCircle2, MapPin, Sparkles, Loader2 } from "lucide-react";

export const ServiceAreaChecker = ({ compact = false }) => {
  const navigate = useNavigate();
  const [zip, setZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {serviced, city, zip}
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [leadSent, setLeadSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const check = async (e) => {
    e?.preventDefault();
    const clean = zip.replace(/\D/g, "").slice(0, 5);
    if (clean.length !== 5) {
      toast.error("Enter a valid 5-digit ZIP code");
      return;
    }
    setLoading(true);
    setResult(null);
    setLeadSent(false);
    try {
      const { data } = await api.post("/service-area/check", { zip: clean });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email || !lead.phone) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/expansion-leads", { ...lead, zip: result.zip });
      setLeadSent(true);
      toast.success("You're on the list! We'll notify you when we expand.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={compact ? "text-slate-900" : "text-slate-900 rounded-3xl bg-white p-6 sm:p-8 shadow-[0_20px_60px_-15px_rgba(11,42,91,0.35)] border border-slate-100"} data-testid="service-area-checker">
      <div className="flex items-center gap-2 text-boco-green font-bold text-xs tracking-[0.2em]">
        <MapPin className="h-4 w-4" /> SERVICE AREA CHECKER
      </div>
      <h3 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-boco-navy leading-tight">
        DO WE SERVICE YOUR AREA?
      </h3>
      <p className="mt-2 text-slate-600 text-sm sm:text-base">
        Enter your ZIP code to see if BOCO BINS comes to your neighborhood.
      </p>

      <form onSubmit={check} className="mt-5 flex gap-3">
        <Input
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
          placeholder="Enter ZIP code"
          inputMode="numeric"
          className="h-14 text-lg font-semibold text-slate-900 border-slate-300 focus-visible:ring-boco-navy"
          data-testid="zip-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="h-14 shrink-0 rounded-md bg-boco-navy px-6 font-bold text-white hover:bg-boco-navylight transition-colors disabled:opacity-70 flex items-center gap-2"
          data-testid="check-zip-btn"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Check ZIP"}
        </button>
      </form>

      <div aria-live="polite">
        <AnimatePresence mode="wait">
          {result?.serviced && (
            <motion.div
              key="yes"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-5 rounded-2xl bg-boco-greenlight border-2 border-boco-green p-6" data-testid="result-serviced">
                <div className="flex items-center gap-2 text-boco-greendark font-display font-extrabold text-xl">
                  <CheckCircle2 className="h-6 w-6" /> GOOD NEWS! BOCO BINS SERVICES YOUR AREA!
                </div>
                <p className="mt-2 text-boco-navy font-medium">
                  You're in our service area{result.city ? ` (${result.city}, VA)` : ""}. Let's get your trash cans clean!
                </p>
                <button
                  onClick={() => navigate(`/book?zip=${result.zip}`)}
                  className="mt-4 inline-flex items-center rounded-full bg-boco-navy px-6 py-3 font-bold text-white hover:bg-boco-navylight transition-colors"
                  data-testid="book-cleaning-btn"
                >
                  Book Your Cleaning
                </button>
              </div>
            </motion.div>
          )}

          {result && !result.serviced && (
            <motion.div
              key="no"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-5 rounded-2xl bg-amber-50 border-2 border-amber-300 p-6" data-testid="result-not-serviced">
                <div className="flex items-center gap-2 text-amber-700 font-display font-extrabold text-xl">
                  <Sparkles className="h-6 w-6" /> WE'RE NOT THERE YET!
                </div>
                <p className="mt-2 text-slate-700 font-medium">
                  BOCO BINS doesn't currently service this area, but we're growing!
                </p>

                {leadSent ? (
                  <div className="mt-4 rounded-xl bg-white border border-amber-200 p-4 text-boco-navy font-semibold" data-testid="lead-success">
                    Thanks! We'll email you the moment we reach ZIP {result.zip}.
                  </div>
                ) : (
                  <form onSubmit={submitLead} className="mt-4 grid gap-3 sm:grid-cols-3">
                    <Input placeholder="Name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="bg-white text-slate-900" data-testid="lead-name" />
                    <Input placeholder="Email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="bg-white text-slate-900" data-testid="lead-email" />
                    <Input placeholder="Phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="bg-white text-slate-900" data-testid="lead-phone" />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="sm:col-span-3 rounded-full bg-boco-green px-6 py-3 font-bold text-boco-navy hover:bg-boco-greendark hover:text-white transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                      data-testid="notify-me-btn"
                    >
                      {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Notify Me When You Expand"}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
